// Invoice validators

import { z } from 'zod';
import { dateSchema, currencyCodeSchema, invoiceStatusEnumSchema } from '../utils/validation';

// Invoice item validator
export const invoiceItemSchema = z.object({
  description: z
    .string()
    .min(1, 'Description is required')
    .max(500, 'Description cannot exceed 500 characters'),
  quantity: z.number().positive('Quantity must be positive'),
  unitPrice: z.number().positive('Unit price must be positive'),
  totalPrice: z.number().positive('Total price must be positive'),
  taxRate: z.number().min(0).max(100),
  discountAmount: z.number().min(0).optional(),
  discountPercentage: z.number().min(0).max(100).optional(),
  productId: z.string().optional(),
  serviceId: z.string().optional(),
  groupName: z.string().max(120, 'Group name cannot exceed 120 characters').optional(),
});

// Create invoice validator
export const createInvoiceSchema = z.object({
  customerId: z.string().min(1, 'Customer ID is required'),
  billToContactId: z.string().optional(),
  projectId: z.string().optional(),
  type: z
    .enum(['standard', 'proforma', 'recurring', 'credit_note', 'debit_note'])
    .default('standard'),
  issueDate: dateSchema,
  dueDate: dateSchema,
  items: z.array(invoiceItemSchema).min(1, 'At least one item is required'),
  paymentTerm: z.object({
    paymentDueDays: z.number().int().min(1).max(365),
    currency: currencyCodeSchema,
    paymentMethods: z.array(z.string()),
    creditLimit: z.number().positive().optional(),
  }),
  currency: currencyCodeSchema.optional(),
  isRecurring: z.boolean().optional(),
  recurringSchedule: z.enum(['monthly', 'yearly']).optional(),
  notes: z.string().max(1000, 'Notes cannot exceed 1000 characters').optional(),
  applyPph23: z.boolean().optional(),
  pph23Rate: z.number().min(0).max(100).optional(),
  pph23GrossUp: z.boolean().optional(),
  withholdingType: z.enum(['PPH23', 'PPH21']).optional(),
  discount: z
    .object({
      name: z.string().max(200),
      amount: z.number().min(0),
    })
    .optional(),
});

// Update invoice validator
export const updateInvoiceSchema = z.object({
  customerId: z.string().min(1, 'Customer ID is required').optional(),
  billToContactId: z.string().optional(),
  projectId: z.string().optional(),
  issueDate: dateSchema.optional(),
  dueDate: dateSchema.optional(),
  items: z.array(invoiceItemSchema).min(1, 'At least one item is required').optional(),
  currency: currencyCodeSchema.optional(),
  isRecurring: z.boolean().optional(),
  recurringSchedule: z.enum(['monthly', 'yearly']).optional(),
  notes: z.string().max(1000, 'Notes cannot exceed 1000 characters').optional(),
  status: invoiceStatusEnumSchema.optional(),
  applyPph23: z.boolean().optional(),
  pph23Rate: z.number().min(0).max(100).optional(),
  pph23GrossUp: z.boolean().optional(),
  withholdingType: z.enum(['PPH23', 'PPH21']).optional(),
  discount: z
    .object({
      name: z.string().max(200),
      amount: z.number().min(0),
    })
    .optional(),
});

// Invoice filter validator
export const invoiceFilterSchema = z.object({
  customerId: z.string().optional(),
  projectId: z.string().optional(),
  status: invoiceStatusEnumSchema.optional(),
  dateFrom: dateSchema.optional(),
  dateTo: dateSchema.optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(100).optional(),
  sortBy: z.enum(['invoiceNumber', 'issueDate', 'dueDate', 'amount']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

// Recurring invoice validator
export const recurringInvoiceSchema = z.object({
  customerId: z.string().min(1, 'Customer ID is required'),
  billToContactId: z.string().optional(),
  projectId: z.string().optional(),
  items: z.array(invoiceItemSchema).min(1, 'At least one item is required'),
  currency: currencyCodeSchema.optional(),
  schedule: z.enum(['monthly', 'yearly']),
  startDate: dateSchema,
  endDate: dateSchema.optional(),
  maxOccurrences: z.number().int().min(1).optional(),
  notes: z.string().max(1000, 'Notes cannot exceed 1000 characters').optional(),
});

// Invoice status update validator
export const invoiceStatusSchema = z.object({
  status: invoiceStatusEnumSchema,
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
});

// Invoice numbering validator
export const invoiceNumberingSchema = z.object({
  prefix: z.string().max(10, 'Prefix cannot exceed 10 characters').optional(),
  padding: z.number().int().min(1).max(10).optional(),
  startNumber: z.number().int().min(1).optional(),
});

// Payment allocation validator
export const allocatePaymentSchema = z.object({
  paymentId: z.string().min(1, 'Payment ID is required'),
  allocations: z
    .array(
      z.object({
        invoiceId: z.string().min(1, 'Invoice ID is required'),
        amount: z.number().positive('Allocation amount must be positive'),
      })
    )
    .min(1, 'At least one allocation is required'),
});

// Types
export type InvoiceItemInput = z.infer<typeof invoiceItemSchema>;
export type CreateInvoiceInput = z.infer<typeof createInvoiceSchema>;
export type UpdateInvoiceInput = z.infer<typeof updateInvoiceSchema>;
export type InvoiceFilterInput = z.infer<typeof invoiceFilterSchema>;
export type RecurringInvoiceInput = z.infer<typeof recurringInvoiceSchema>;
export type InvoiceStatusInput = z.infer<typeof invoiceStatusSchema>;
export type InvoiceNumberingInput = z.infer<typeof invoiceNumberingSchema>;
export type AllocatePaymentInput = z.infer<typeof allocatePaymentSchema>;
